import { Button, ChevronDownIcon, CloseIcon, DownloadIcon, Modal } from '@st/theme'
import { useId, useState } from 'react'
import { useProcess } from '~/store'
import { ContractDocumentViewport } from './contract-document-viewport'
import type { ContractViewerDocument } from './contract-signing-shell-module'
import { SigningSectionsNav } from './signing-sections-nav'

/** Read-only view of the submitted, signed document. */
export function SignedContractModal({ document }: { document: ContractViewerDocument }) {
  const contractSigning = useProcess('contractSigning')
  const sectionsId = useId()
  const [sectionsOpen, setSectionsOpen] = useState(false)
  const close = () => contractSigning.send({ type: 'signedDocumentClosed' })

  return (
    <Modal isOpen={true} onClose={close}>
      <div className="fixed inset-0 grid grid-rows-[auto,1fr] overflow-hidden bg-white">
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3 sm:px-6 sm:py-4">
          <Button variant="subtle" leadingIcon={<CloseIcon className="h-4 w-4" />} onClick={close}>
            Close
          </Button>
          <div className="min-w-0 truncate text-base font-semibold text-gray-900">
            {document.name}
          </div>
          {document.pageGroups.length > 0 && (
            <Button
              variant="subtle"
              className="lg:hidden"
              aria-expanded={sectionsOpen}
              aria-controls={sectionsId}
              trailingIcon={<ChevronDownIcon className="h-4 w-4" />}
              onClick={() => setSectionsOpen(!sectionsOpen)}
            >
              Sections
            </Button>
          )}
          <Button
            variant="default"
            leadingIcon={<DownloadIcon className="h-4 w-4" />}
            href={document.downloadUrl!}
          >
            Download
          </Button>
        </div>
        <div className="relative flex min-h-0 min-w-0 overflow-hidden">
          <SigningSectionsNav
            id={sectionsId}
            pageGroups={document.pageGroups}
            isOpen={sectionsOpen}
            onClose={() => setSectionsOpen(false)}
            onSelect={(group) =>
              contractSigning.send({ type: 'bookmarkSelected', pageNumber: group.pageRange.first })
            }
          />
          <div className="min-h-0 min-w-0 flex-1 overflow-hidden bg-stone-500">
            <ContractDocumentViewport document={document} />
          </div>
        </div>
      </div>
    </Modal>
  )
}
